// The shape of one animation frame: a typed context and an ordered list of the
// blocks that run over it. `src/world/frame.ts` is a different frame - the
// camera-relative scene root. The context is the sim's unless a list names its
// own, as the Atlas's tick does (`src/atlas/frame.ts`).

/** What one frame is: the seconds it carries and the branch it belongs to. */
export interface FrameCtx {
  /** Wall seconds since the last frame, clamped the way this frame's branch clamps it. */
  realDt: number;
  /** Sim seconds: `realDt` through the warp rate, or 0 while a guidance solve holds the clock. */
  dt: number;
  /** `clock.elapsedTime` once the frame's own delta has been drained. */
  t: number;
  /** The editor overlay covers the sim canvas, so the frame runs nothing. */
  editorOpen: boolean;
  /** A menu, the key list or the land verb: the sim is paused and a stage may have the canvas. */
  paused: boolean;
  /** The land verb: the Atlas has the camera and the ship rides its parent alone. */
  landOpen: boolean;
  /** Neither of the two above - the branch that flies, draws and updates the HUD. */
  live: boolean;
}

export interface FrameSystem<C = FrameCtx> {
  /** Unique across the list, and the profiler label when `profile` is set. */
  name: string;
  /** The frames this block runs on. No predicate means every frame. */
  when?: (c: C) => boolean;
  update: (c: C) => void;
  /** Time it under `name`. Only blocks that already carried a mark set it, so the perf panel's labels do not change. */
  profile?: boolean;
}

/** The pair `runSystems` needs, so this module does not import the profiler itself. */
export interface FrameProfiler {
  mark(label: string): void;
  end(label: string): void;
}

export function runSystems<C>(
  list: readonly FrameSystem<C>[], ctx: C, profiler: FrameProfiler,
): void {
  for (const s of list) {
    if (s.when && !s.when(ctx)) continue;
    if (!s.profile) {
      s.update(ctx);
      continue;
    }
    profiler.mark(s.name);
    s.update(ctx);
    profiler.end(s.name);
  }
}

/** A repeated name would run a block twice and fold both into one profiler row. */
export function assertUniqueNames<C>(list: readonly FrameSystem<C>[]): void {
  const seen = new Set<string>();
  for (const s of list) {
    if (seen.has(s.name)) throw new Error(`frame system "${s.name}" is listed twice`);
    seen.add(s.name);
  }
}

/** Where `spliceSystems` puts its entries: right before or right after one named block. */
export type SplicePoint<C> =
  | { before: string; after?: never; add: readonly FrameSystem<C>[] }
  | { after: string; before?: never; add: readonly FrameSystem<C>[] };

/**
 * A copy of `list` with `add` inserted at a named block. A name the list lacks
 * throws: a splice that fell through would drop its entries from every frame.
 */
export function spliceSystems<C>(list: readonly FrameSystem<C>[], at: SplicePoint<C>): FrameSystem<C>[] {
  const name = at.before ?? at.after;
  const i = list.findIndex((s) => s.name === name);
  if (i < 0) throw new Error(`frame system "${name}" is not in the list to splice into`);
  const cut = at.before !== undefined ? i : i + 1;
  return [...list.slice(0, cut), ...at.add, ...list.slice(cut)];
}
